/**
 * Pure realm routing logic for the auth/realm lifecycle.
 *
 * `getRealm` derives the active realm from pathname + role (no Redux). The URL
 * picks distributor vs standard; role picks which standard realm applies.
 *
 * `getRouteRealmAccess` is a separate per-route-tree guard that checks whether
 * the derived realm is allowed for the current route configuration.
 *
 * Lifecycle position: … → useAuthUser (getRealm) → AuthenticatedLayout (shell) → RealmGuard (getRouteRealmAccess) → …
 */
import {
  DISTRIBUTOR_HOME_URL,
  STANDARD_HOME_URL,
  isPathForDistributorRealm,
} from "@features/navigation/helpers";
import { Realm, validRealms } from "@models/Realm";
import { UserRole } from "@models/UserRole";

export type RouteRealmAccess =
  | { type: "redirect"; to: string }
  | { type: "forbidden" }
  | { type: "ok" };

// Always returns an authorized realm for the user role.
export const getRealm = (pathname: string, role: UserRole): Realm => {
  const userRealms = validRealms[role];

  if (
    isPathForDistributorRealm(pathname) &&
    userRealms.includes(Realm.Distributor)
  ) {
    return Realm.Distributor;
  }

  // Outside the distributor path branch, today's role definitions provide at
  // most one non-distributor realm. Distributor-only roles fall back to their
  // default realm and are rejected by the RealmGuard (gives user a 404).
  const standardRealm = userRealms.find((r) => r !== Realm.Distributor);

  // If no standard realm, use the first authorized realm.
  // Eg. distributor-orderer tries to access an internal path, standardRealm would be undefined
  // so we use the first authorized realm: "dist"
  return standardRealm ?? userRealms[0];
};

export const getRouteRealmAccess = (
  pathname: string,
  role: UserRole,
  routeRealms: readonly Realm[]
): RouteRealmAccess => {
  const realm = getRealm(pathname, role);
  if (routeRealms.includes(realm)) return { type: "ok" };
  if (
    pathname === STANDARD_HOME_URL &&
    validRealms[role]?.includes(Realm.Distributor)
  ) {
    return { type: "redirect", to: DISTRIBUTOR_HOME_URL };
  }
  return { type: "forbidden" };
};
